// Pillows site footer rendered in-tree by PageSkeleton (#33285 P3/P5).
//
// The footer navigation is resolved server-side from the "footer" NavMenu into
// typed columns (chrome._build_footer_nav) and rendered here as a real React
// tree -- the .footer-nav desktop + .footer-nav-mobile mobile <nav> blocks.
// Every link carries an explicit target, so the shared links.ts widget (which
// only rewrites untargeted links) leaves them alone: no pre-hydration DOM
// mutation, no hydration mismatch. Each mobile section owns its collapse state
// (MobileFooterBlock below). Only footer_copy_html remains injected -- it is
// the FooterSettings RichText StreamField (the CMS-RichText exception).
import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

type FooterNavItem = {
    block_type: string;
    label: string;
    url: string;
    target: string;
    html: string;
};

type FooterNavColumn = {
    title: string;
    items: readonly FooterNavItem[];
};

type IProps = Immutable<{
    phone_display: string;
    phone_href: string;
    copyright_year: string;
    footer_copy_html: string;
    footer_nav: readonly FooterNavColumn[];
    staticBase: string;
}>;

// One collapsible mobile footer section. Clicking the header toggles
// `footer-nav-mobile__block--active` on the block (component state), which the
// footer SCSS uses to reveal the dropdown.
const MobileFooterBlock = (
    props: Immutable<{ column: FooterNavColumn }>,
): React.JSX.Element => {
    const { column } = props;
    const [active, setActive] = React.useState(false);
    return (
        <div
            className={
                active
                    ? "footer-nav-mobile__block footer-nav-mobile__block--active"
                    : "footer-nav-mobile__block"
            }
        >
            <h3
                className="footer-nav-mobile__header site-footer__header"
                role="button"
                tabIndex={0}
                aria-expanded={active}
                onClick={() => {
                    setActive((value) => !value);
                }}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setActive((value) => !value);
                    }
                }}
            >
                {column.title}
            </h3>
            <div className="site-footer__dropdown">
                {column.items.map((item, ii) =>
                    item.block_type === "html" ? (
                        // Raw-HTML block (the OneTrust cookie-settings button).
                        // The OneTrust SDK mutates this button in place before
                        // hydration, so suppress the mismatch warning on this
                        // externally-managed opaque subtree.
                        <div
                            key={ii}
                            suppressHydrationWarning
                            dangerouslySetInnerHTML={{
                                __html: item.html,
                            }}
                        />
                    ) : (
                        <div
                            className="footer-nav__item footer-nav__item--mobile"
                            key={ii}
                        >
                            <a
                                className="footer-nav__item--mobile-link"
                                href={item.url}
                                target={item.target}
                            >
                                {item.label}
                            </a>
                        </div>
                    ),
                )}
            </div>
        </div>
    );
};

export const SiteFooter = (props: IProps): React.JSX.Element => {
    const {
        phone_display,
        phone_href,
        copyright_year,
        footer_copy_html,
        footer_nav,
        staticBase,
    } = props;
    const a360Alt = t`An Accessible360 badge sharing that we've partnered with an industry-leading ADA vendor who maintains our site is in good standing with accessibility.`;
    return (
        <footer className="site-footer l-full-width">
            <div className="l-capped-width">
                <div className="site-footer__header site-footer__header-container">
                    <div className="site-footer__header__phone-number">
                        {phone_display && (
                            <a href={phone_href}>
                                <div className="button site-footer__header__button site-footer__header__phone-number-icon">
                                    {phone_display}
                                </div>
                            </a>
                        )}
                    </div>
                </div>

                <nav className="footer-nav l-capped-width">
                    {footer_nav.map((column, ci) => (
                        <div className="footer-nav__col" key={ci}>
                            <ul className="footer-nav__list">
                                <h3
                                    className="site-footer__header"
                                    role="heading"
                                    aria-level={2}
                                >
                                    {column.title}
                                </h3>
                                {column.items.map((item, ii) =>
                                    item.block_type === "html" ? (
                                        // Raw-HTML block (the OneTrust cookie-
                                        // settings button). The OneTrust SDK
                                        // mutates this button in place before
                                        // hydration, so suppress the mismatch
                                        // warning on this externally-managed
                                        // opaque subtree.
                                        <div
                                            key={ii}
                                            suppressHydrationWarning
                                            dangerouslySetInnerHTML={{
                                                __html: item.html,
                                            }}
                                        />
                                    ) : (
                                        <li
                                            className="footer-nav__item"
                                            key={ii}
                                        >
                                            <a
                                                href={item.url}
                                                target={item.target}
                                            >
                                                {item.label}
                                            </a>
                                        </li>
                                    ),
                                )}
                            </ul>
                        </div>
                    ))}
                </nav>

                <nav className="footer-nav-mobile u-margin-bottom--xl">
                    {footer_nav.map((column, ci) => (
                        <MobileFooterBlock key={ci} column={column} />
                    ))}
                </nav>

                <div className="footer-badges l-capped-width">
                    <div className="footer-badges__item footer-badges__item--a360">
                        <a
                            href="https://allyant.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <img
                                className="responsive-img"
                                src={`${staticBase}img/icons/badge-A360.png`}
                                alt={a360Alt}
                                loading="lazy"
                                decoding="async"
                            />
                        </a>
                    </div>
                </div>

                <div className="site-footer__legal">
                    &copy; {copyright_year} Tempur-Pedic North America
                    LLC.&nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;
                    <a href="/legal/privacy-policy/">{t`Privacy Policy`}</a>
                    &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;
                    <a href="/legal/terms-of-use/">{t`Terms & Conditions`}</a>
                    <div
                        className="footer-legal__aux-copy"
                        dangerouslySetInnerHTML={{ __html: footer_copy_html }}
                    />
                </div>
            </div>
        </footer>
    );
};
